import {
  Children,
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  type ReactElement,
  type ReactNode,
} from "react"
import { tracker } from "~/common/analytics"

export type AnalyticsContextProps = {
  ability?: string
  category?: string
  prefix?: string
  uId?: string
  uName?: string
  upgrade?: string
}

export type AnalyticsSendFunction = (
  action: string,
  fields?: Record<string, string>,
) => void

type AnalyticsActionDelegateFunction = (
  ...args: unknown[]
) => false | null | string | undefined

type AnalyticsActionDelegate = string | true | AnalyticsActionDelegateFunction

type AnalyticsActionDelegates = Record<
  string,
  AnalyticsActionDelegate | unknown
>

type AnalyticsActionBaseProps = AnalyticsContextProps & {
  action?: AnalyticsActionDelegate
  children: ReactElement
  fields?: Record<string, string>
}

type AnalyticsActionProps = AnalyticsActionBaseProps & Record<string, unknown>

export type AnalyticsActionRef = {
  send: AnalyticsSendFunction
}

const AnalyticsPropsContext = createContext<AnalyticsContextProps>({})

const contextKeys: Array<keyof AnalyticsContextProps> = [
  "ability",
  "category",
  "prefix",
  "uId",
  "uName",
  "upgrade",
]

const mergeContext = (
  current: AnalyticsContextProps,
  parent: AnalyticsContextProps,
): AnalyticsContextProps => {
  const merged: AnalyticsContextProps = {
    ...parent,
  }

  contextKeys.forEach((key) => {
    const value = current[key]

    if (typeof value === "undefined") return

    merged[key] = value
  })

  return merged
}

const getContextValues = (props?: AnalyticsContextProps) => {
  return contextKeys.map((key) => props?.[key])
}

const filterActionName = (actionName: string) => {
  return actionName.replace(/[^a-zA-Z0-9\u4e00-\u9fa5 ]/g, "")
}

const isDelegate = (value: unknown) => {
  return (
    value === true || typeof value === "function" || typeof value === "string"
  )
}

const tryGetTargetAction = (event: unknown) => {
  const currentTarget =
    typeof event === "object" && event !== null
      ? (event as { currentTarget?: { innerText?: unknown } }).currentTarget
      : undefined
  const innerText =
    typeof currentTarget?.innerText === "string"
      ? filterActionName(currentTarget.innerText)
      : ""

  if (!innerText) {
    console.warn(
      "AnalyticsAction 的 true 代理仅适用于带有 innerText 的目标元素",
    )
  }

  return innerText
}

const toSafeFieldValue = (value: string) => {
  const stringPrototype: object = String.prototype

  if (
    "toWellFormed" in stringPrototype &&
    typeof stringPrototype.toWellFormed === "function"
  ) {
    return stringPrototype.toWellFormed.call(value)
  }

  return value.replace(
    /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g,
    "\uFFFD",
  )
}

const getDelegateHandlers = (
  send: AnalyticsSendFunction,
  delegates: AnalyticsActionDelegates,
  originProps: Record<string, unknown>,
  fields?: Record<string, string>,
) => {
  const handlers: Record<string, (...args: unknown[]) => void> = {}

  Object.entries(delegates).forEach(([key, delegate]) => {
    if (!isDelegate(delegate)) return

    const trackedDelegate = delegate as AnalyticsActionDelegate
    const origin = originProps[key]

    handlers[key] = (...args: unknown[]) => {
      let action: false | null | string | true | undefined

      if (typeof trackedDelegate === "function") {
        action = trackedDelegate(...args)
      } else {
        action = trackedDelegate
      }

      if (action) {
        send(action === true ? tryGetTargetAction(args[0]) : action, fields)
      }

      if (typeof origin === "function") {
        const originHandler = origin as (...originArgs: unknown[]) => void
        originHandler(...args)
      }
    }
  })

  return handlers
}

const useAnalyticsSendFromContext = (
  context: AnalyticsContextProps,
): AnalyticsSendFunction => {
  const {
    ability = "",
    category = "",
    prefix = "",
    uId = "",
    uName = "",
    upgrade = "",
  } = context

  return useCallback(
    (action: string, fields?: Record<string, string>) => {
      const mixedFields: Record<string, string> = {
        prefix,
        category,
        uId,
        uName,
        ...fields,
      }

      if (ability) {
        mixedFields.ability = ability
      }

      if (upgrade) {
        mixedFields.upgrade = upgrade
      }

      try {
        tracker.action(action, category, mixedFields)
      } catch {
        console.warn("analytics send error")

        Object.keys(mixedFields).forEach((key) => {
          mixedFields[key] = toSafeFieldValue(mixedFields[key])
        })

        tracker.action(action, category, mixedFields)
      }
    },
    [ability, category, prefix, uId, uName, upgrade],
  )
}

export const useAnalyticsContext = (props?: AnalyticsContextProps) => {
  const parent = useContext(AnalyticsPropsContext)

  return useMemo(() => {
    return props ? mergeContext(props, parent) : parent
  }, [parent, ...getContextValues(props)])
}

export const useAnalyticsEvent = (
  props?: AnalyticsContextProps,
): AnalyticsSendFunction => {
  const context = useAnalyticsContext(props)

  return useAnalyticsSendFromContext(context)
}

export const AnalyticsContext = ({
  children,
  ...others
}: AnalyticsContextProps & { children: ReactNode }) => {
  const mergedContext = useAnalyticsContext(others)

  return (
    <AnalyticsPropsContext.Provider value={mergedContext}>
      {children}
    </AnalyticsPropsContext.Provider>
  )
}

export const AnalyticsAction = forwardRef<
  AnalyticsActionRef,
  AnalyticsActionProps
>((props, ref) => {
  const { action, children, fields } = props as AnalyticsActionBaseProps
  const contextProps: AnalyticsContextProps = {}
  const delegates: AnalyticsActionDelegates = { ...props }

  contextKeys.forEach((key) => {
    contextProps[key] = props[key] as string | undefined
    delete delegates[key]
  })
  delete delegates.action
  delete delegates.children
  delete delegates.fields

  const child = Children.only(children)
  const context = useAnalyticsContext(contextProps)
  const send = useAnalyticsSendFromContext(context)

  useImperativeHandle(ref, () => ({ send }), [send])

  const eventDelegates = action
    ? {
        onClick: action,
        ...delegates,
      }
    : delegates

  if (process.env.NODE_ENV === "development" && !isValidElement(child)) {
    console.warn("AnalyticsAction only can be applied to a jsx element")
  }

  if (!isValidElement<Record<string, unknown>>(child)) {
    return child
  }

  const additionalProps = getDelegateHandlers(
    send,
    eventDelegates,
    child.props,
    fields,
  )

  return cloneElement(child, additionalProps)
})

AnalyticsAction.displayName = "AnalyticsAction"
